import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Paper,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  ZoomIn,
  ZoomOut,
  Fullscreen,
  Close,
  ErrorOutline,
  CheckCircle,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

interface SubQuestion {
  id?: string;
  question: string;
  options: string[];
  correctOption?: string;
  required?: boolean;
}

interface ReferenceReadingQuestion {
  id: string;
  type: string;
  question: string;
  asset?: string;
  options?: string[];
  correctAnswer?: number;
  questions?: SubQuestion[];
  points?: number;
  [key: string]: any;
}

interface ReferenceReadingQuestionProps {
  question: ReferenceReadingQuestion;
  userAnswer?: any;
  onAnswerChange: (answer: any) => void;
  onComplete?: (isCorrect: boolean, timeSpent: number) => void;
  disabled?: boolean;
  reviewMode?: boolean;
}

const ReferenceReadingQuestionComponent: React.FC<
  ReferenceReadingQuestionProps
> = ({
  question,
  userAnswer,
  onAnswerChange,
  onComplete,
  disabled = false,
  reviewMode = false,
}) => {
  const theme = useTheme();
  const startTimeRef = useRef(Date.now());
  const hasCompletedRef = useRef(false);

  // State
  const [loaded, setLoaded] = useState(false);
  const [assetError, setAssetError] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Determine question structure
  const isAudio = !!question.asset?.match(/\.(mp3|m4a|wav|ogg)$/i);
  const hasMultipleQuestions =
    Array.isArray(question.questions) && question.questions.length > 0;
  const isSingleQuestion =
    !hasMultipleQuestions && Array.isArray(question.options);

  // Get the current answers directly from userAnswer prop
  const getCurrentAnswers = useCallback(() => {
    if (!userAnswer) {
      return hasMultipleQuestions ? {} : { single: "" };
    }

    if (hasMultipleQuestions) {
      // Ensure we're returning an object for multiple questions
      if (typeof userAnswer === "object" && !Array.isArray(userAnswer)) {
        return userAnswer;
      }
      // Convert other formats to object
      return {};
    } else {
      // For single questions
      if (typeof userAnswer === "string") {
        return { single: userAnswer };
      } else if (
        typeof userAnswer === "object" &&
        userAnswer.single !== undefined
      ) {
        return userAnswer;
      }
      return { single: "" };
    }
  }, [userAnswer, hasMultipleQuestions]);

  // FIXED: Calculate progress with consistent ID format
  const calculateProgress = useCallback(() => {
    const answers = getCurrentAnswers();

    if (hasMultipleQuestions && question.questions) {
      const totalQuestions = question.questions.length;
      let answeredCount = 0;
      const unansweredList: string[] = [];

      question.questions.forEach((subQ, index) => {
        // CRITICAL: Use consistent ID format
        const questionId = subQ.id || `q${index}`;
        const hasAnswer =
          answers[questionId] !== undefined &&
          answers[questionId] !== null &&
          answers[questionId] !== "";

        // Debug logging
        console.log(`Progress check for ${questionId}:`, {
          answer: answers[questionId],
          hasAnswer: hasAnswer,
        });

        if (hasAnswer) {
          answeredCount++;
        } else {
          unansweredList.push(`${index + 1}) ${subQ.question}`);
        }
      });

      return {
        answered: answeredCount,
        total: totalQuestions,
        percentage: (answeredCount / totalQuestions) * 100,
        isComplete: answeredCount === totalQuestions,
        unanswered: unansweredList,
      };
    } else if (isSingleQuestion) {
      const isAnswered = !!answers.single;
      return {
        answered: isAnswered ? 1 : 0,
        total: 1,
        percentage: isAnswered ? 100 : 0,
        isComplete: isAnswered,
        unanswered: isAnswered ? [] : [question.question],
      };
    }

    return {
      answered: 0,
      total: 0,
      percentage: 0,
      isComplete: false,
      unanswered: [],
    };
  }, [getCurrentAnswers, hasMultipleQuestions, isSingleQuestion, question]);

  // Check completion and notify parent
  useEffect(() => {
    const progress = calculateProgress();

    // Only call onComplete once when all questions are answered
    if (progress.isComplete && onComplete && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      const timeSpent = Date.now() - startTimeRef.current;

      if (hasMultipleQuestions && question.questions) {
        const answers = getCurrentAnswers();
        const correctCount = question.questions.filter((subQ, index) => {
          const questionId = subQ.id || `q${index}`;
          return answers[questionId] === subQ.correctOption;
        }).length;
        onComplete(correctCount === question.questions.length, timeSpent);
      } else if (isSingleQuestion) {
        const answers = getCurrentAnswers();
        const isCorrect =
          question.correctAnswer !== undefined &&
          question.options?.[question.correctAnswer] === answers.single;
        onComplete(isCorrect, timeSpent);
      }
    } else if (!progress.isComplete) {
      // Reset completion flag if user removes an answer
      hasCompletedRef.current = false;
    }
  }, [
    calculateProgress,
    getCurrentAnswers,
    hasMultipleQuestions,
    isSingleQuestion,
    question,
    onComplete,
  ]);

  // FIXED: Handle answer changes with consistent IDs
  const handleSubQuestionChange = useCallback(
    (questionId: string, value: string) => {
      const currentAnswers = getCurrentAnswers();

      if (hasMultipleQuestions) {
        const newAnswers = { ...currentAnswers };

        // Set or remove the answer based on value
        if (value !== undefined && value !== null && value !== "") {
          newAnswers[questionId] = value;
        } else {
          // Remove empty answers to ensure proper tracking
          delete newAnswers[questionId];
        }

        // Debug logging
        console.log("Setting answer:", {
          questionId,
          value,
          newAnswers,
        });

        onAnswerChange(newAnswers);
      } else {
        onAnswerChange(value);
      }
    },
    [getCurrentAnswers, hasMultipleQuestions, onAnswerChange]
  );

  // Asset handlers
  const handleAssetLoaded = useCallback(() => {
    setLoaded(true);
    setAssetError(false);
  }, []);

  const handleAssetError = useCallback(() => {
    setAssetError(true);
    setLoaded(false);
  }, []);

  // Zoom handlers
  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  const handleZoomOut = () =>
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoomLevel(1);

  // Reset completion flag when question changes
  useEffect(() => {
    hasCompletedRef.current = false;
    startTimeRef.current = Date.now();
  }, [question.id]);

  // Validation
  if (!question) {
    return <Alert severity="error">Invalid question data</Alert>;
  }

  if (!question.asset && !isSingleQuestion && !hasMultipleQuestions) {
    return (
      <Alert severity="error">Question must have an asset or options</Alert>
    );
  }

  const progress = calculateProgress();
  const answers = getCurrentAnswers();

  // Render reference material (image/audio)
  const renderReferenceMaterial = () => {
    if (!question.asset) return null;

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Reference Material
          </Typography>

          <Box
            sx={{
              position: "relative",
              width: "100%",
              minHeight: isAudio ? "auto" : 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.palette.grey[100],
              borderRadius: 1,
              overflow: "hidden",
              cursor: !isAudio && loaded ? "zoom-in" : "default",
            }}
            onClick={
              !isAudio && loaded ? () => setFullscreenOpen(true) : undefined
            }
          >
            {assetError ? (
              <Box sx={{ textAlign: "center", p: 4 }}>
                <ErrorOutline
                  sx={{ fontSize: 48, color: theme.palette.error.main, mb: 2 }}
                />
                <Typography color="error">Failed to load resource</Typography>
                <Typography variant="body2" color="text.secondary">
                  {question.asset}
                </Typography>
              </Box>
            ) : isAudio ? (
              <audio
                controls
                controlsList="nodownload"
                src={question.asset}
                style={{ width: "100%", maxWidth: 500 }}
                onLoadedData={handleAssetLoaded}
                onError={handleAssetError}
              >
                Your browser does not support the audio element.
              </audio>
            ) : (
              <>
                {!loaded && <CircularProgress />}
                <img
                  src={question.asset}
                  alt="Reference"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    display: loaded ? "block" : "none",
                    transform: `scale(${zoomLevel})`,
                    transition: "transform 0.3s ease",
                  }}
                  onLoad={handleAssetLoaded}
                  onError={handleAssetError}
                />
              </>
            )}
          </Box>

          {!isAudio && loaded && (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 1 }}
            >
              <IconButton onClick={handleZoomOut} disabled={zoomLevel <= 0.5}>
                <ZoomOut />
              </IconButton>
              <Chip label={`${Math.round(zoomLevel * 100)}%`} />
              <IconButton onClick={handleZoomIn} disabled={zoomLevel >= 3}>
                <ZoomIn />
              </IconButton>
              <IconButton onClick={handleResetZoom}>
                <Fullscreen />
              </IconButton>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // FIXED: Render sub-question with consistent ID format
  const renderSubQuestion = (subQ: any, index: number) => {
    // CRITICAL: Use consistent ID format that matches validation
    const questionId = subQ.id || `q${index}`;
    const selectedAnswer = answers[questionId] || "";

    // Debug logging
    console.log(`Rendering question ${index + 1} with ID "${questionId}":`, {
      question: subQ.question,
      currentAnswer: selectedAnswer,
      allAnswers: answers,
    });

    return (
      <Paper key={questionId} elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {index + 1}) {subQ.question}
          {subQ.required === false && (
            <Chip
              label="Optional"
              size="small"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>

        <RadioGroup
          value={selectedAnswer}
          onChange={(e) => {
            console.log(
              `Setting answer for "${questionId}" to:`,
              e.target.value
            );
            handleSubQuestionChange(questionId, e.target.value);
          }}
        >
          {subQ.options.map((option: string, optionIndex: number) => (
            <FormControlLabel
              key={`${questionId}-option-${optionIndex}`}
              value={option}
              control={<Radio disabled={disabled || reviewMode} />}
              label={option}
              sx={{
                mb: 1,
                p: 1,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  reviewMode && option === subQ.correctOption
                    ? theme.palette.success.light + "40"
                    : selectedAnswer === option
                    ? theme.palette.primary.light + "20"
                    : "transparent",
                "&:hover":
                  !disabled && !reviewMode
                    ? { backgroundColor: theme.palette.action.hover }
                    : {},
              }}
            />
          ))}
        </RadioGroup>

        {reviewMode &&
          selectedAnswer &&
          selectedAnswer !== subQ.correctOption && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Correct answer: {subQ.correctOption}
            </Alert>
          )}
      </Paper>
    );
  };

  // Render single question
  const renderSingleQuestion = () => {
    if (!isSingleQuestion || !question.options) return null;

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <RadioGroup
          value={answers.single || ""}
          onChange={(e) => handleSubQuestionChange("single", e.target.value)}
        >
          {question.options.map((option: string, index: number) => (
            <FormControlLabel
              key={`option-${index}`}
              value={option}
              control={<Radio disabled={disabled || reviewMode} />}
              label={option}
              sx={{
                mb: 1,
                p: 1,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  reviewMode && question.correctAnswer === index
                    ? theme.palette.success.light + "40"
                    : answers.single === option
                    ? theme.palette.primary.light + "20"
                    : "transparent",
                "&:hover":
                  !disabled && !reviewMode
                    ? { backgroundColor: theme.palette.action.hover }
                    : {},
              }}
            />
          ))}
        </RadioGroup>

        {reviewMode &&
          answers.single &&
          question.correctAnswer !== undefined && (
            <Alert
              severity={
                answers.single === question.options[question.correctAnswer]
                  ? "success"
                  : "info"
              }
              sx={{ mt: 2 }}
            >
              {answers.single === question.options[question.correctAnswer]
                ? "Correct!"
                : `Correct answer: ${question.options[question.correctAnswer]}`}
            </Alert>
          )}
      </Paper>
    );
  };

  return (
    <Box>
      {/* Main Question */}
      <Typography variant="h6" gutterBottom>
        {question.question}
      </Typography>

      {/* Progress Indicator */}
      {hasMultipleQuestions && (
        <Card sx={{ mb: 3, p: 2, bgcolor: "background.default" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Progress: {progress.answered} of {progress.total} questions
              answered
            </Typography>
            <Chip
              size="small"
              label={
                progress.isComplete
                  ? "Complete"
                  : `${Math.round(progress.percentage)}%`
              }
              color={progress.isComplete ? "success" : "primary"}
              icon={progress.isComplete ? <CheckCircle /> : undefined}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress.percentage}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "grey.300",
              "& .MuiLinearProgress-bar": {
                bgcolor: progress.isComplete ? "success.main" : "primary.main",
              },
            }}
          />
          {!progress.isComplete && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ mt: 1, display: "block" }}
            >
              Please answer all questions to proceed to the next question
            </Typography>
          )}
        </Card>
      )}

      {/* Reference Material */}
      {renderReferenceMaterial()}

      {/* Questions Section */}
      {hasMultipleQuestions && question.questions ? (
        <Box>
          <Typography variant="body1" sx={{ mb: 2, fontStyle: "italic" }}>
            Answer the following questions based on the reference material:
          </Typography>
          {question.questions.map((subQ, index) =>
            renderSubQuestion(subQ, index)
          )}
        </Box>
      ) : (
        renderSingleQuestion()
      )}

      {/* Fullscreen Dialog for Images */}
      {!isAudio && (
        <Dialog
          open={fullscreenOpen}
          onClose={() => setFullscreenOpen(false)}
          maxWidth={false}
          fullWidth
        >
          <DialogTitle>
            Reference Image
            <IconButton
              onClick={() => setFullscreenOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "70vh",
                p: 2,
              }}
            >
              <img
                src={question.asset}
                alt="Reference Full"
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default ReferenceReadingQuestionComponent;
